import { ChangeEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeartbeat } from "@fortawesome/free-solid-svg-icons";
import CustomInput from "../components/CustomInput";
import CustomButton from "../components/CustomButton";
import Loader from "../components/Loader";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { ParseErrors } from "../utils/ParseErrors";

const Reset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const refId = searchParams.get("ref_id");
  const [values, setValues] = useState({ password: "", confirmPassword: "" });
  const [isLoading, setIsLoading] = useState(false);

  const onChangeValues = (e: ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!refId) {
      toast.error("Invalid reset link");
      return;
    }
    if (!values.password || values.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (values.password !== values.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.put(`/auth/reset-password/${refId}`, {
        password: values.password,
      });
      toast.success("Account verified. You can now sign in.");
      navigate("/auth");
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#EEF3FA] px-4 py-8 sm:px-5">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-[420px] w-[420px] rounded-full bg-[#BFD6FF]/55 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[480px] w-[480px] rounded-full bg-[#D7E8FF]/70 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-[430px] rounded-2xl border border-[#E4EBF5] bg-white px-5 py-8 shadow-[0_20px_50px_rgba(21,44,85,0.08)] sm:px-10 sm:py-10">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1562EE] shadow-[0_12px_30px_rgba(21,98,238,0.28)]">
            <FontAwesomeIcon
              icon={faHeartbeat}
              className="text-[24px] text-white"
            />
          </div>
          <h1 className="font-[Gilroy-Bold] text-[28px] text-[#152C55]">
            Set your password
          </h1>
          <p className="mt-2 font-[Gilroy-Medium] text-[14px] text-[#6B7C99]">
            Create a password to activate your HealthSure account
          </p>
        </div>

        <div className="space-y-5">
          <CustomInput
            label="Password"
            name="password"
            type="password"
            onChange={onChangeValues}
            className="!h-[48px] !rounded-xl !border-[#E4EBF5] !bg-[#F7F9FC] focus:!border-[#1562EE] focus:!bg-white"
          />
          <CustomInput
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            onChange={onChangeValues}
            className="!h-[48px] !rounded-xl !border-[#E4EBF5] !bg-[#F7F9FC] focus:!border-[#1562EE] focus:!bg-white"
          />

          <CustomButton
            variant="primary"
            fullWidth
            disabled={isLoading}
            className="!h-[50px] !rounded-xl !border-none !bg-[#1562EE] !text-[15px] !font-[Gilroy-Medium] hover:!bg-[#1254CC]"
            onClick={handleSubmit}
          >
            {isLoading ? <Loader /> : "Activate Account"}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default Reset;
